import { cn } from '@/lib/utils/cn';
import type { UploadProgress } from '@/lib/api/upload';

/**
 * Upload progress of one file (T-0156): the bar and the line under it.
 *
 * The look is the upload modal's (`FileRowView` in
 * `components/modals/upload-modal.tsx`, prototype `.up-bar` in
 * `modals-round3.js`): a 6px paper-100 track, amber while bytes are sent,
 * at-risk once the last byte has left and the server is processing. The
 * percentage is bytes sent of the total, never an estimate.
 */

/** Whole percent of the bytes sent, 0 to 100. */
export function uploadPercent(progress: UploadProgress | null): number {
  if (!progress) return 0;
  return Math.min(100, Math.max(0, Math.floor(progress.fraction * 100)));
}

/** "Uploading 42%", or "Processing…" once the last byte has been sent. */
export function uploadStatusLabel(progress: UploadProgress | null, processing: boolean): string {
  return processing ? 'Processing…' : `Uploading ${uploadPercent(progress)}%`;
}

export interface UploadProgressBarProps {
  /** What is being sent, for the progress bar's accessible name. */
  fileName: string;
  progress: UploadProgress | null;
  /** The last byte has been sent; the server has not answered yet. */
  processing: boolean;
  className?: string;
}

export function UploadProgressBar({
  fileName,
  progress,
  processing,
  className,
}: UploadProgressBarProps) {
  const percent = processing ? 100 : uploadPercent(progress);
  return (
    <div className={cn('w-full', className)}>
      <div
        role="progressbar"
        aria-label={`Upload of ${fileName}`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-valuetext={uploadStatusLabel(progress, processing)}
        className="h-1.5 w-full overflow-hidden rounded-full bg-paper-100"
      >
        <span
          className={cn('block h-full', processing ? 'bg-posture-at-risk' : 'bg-accent-amber-600')}
          style={{ width: `${percent}%` }}
        />
      </div>
      <div className="mt-1.5 text-meta font-medium text-ink-700">
        {processing ? (
          'Processing…'
        ) : (
          <>
            Uploading <span className="font-mono">{percent}%</span>
          </>
        )}
      </div>
    </div>
  );
}
